import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, MapPin, Mic, ChevronRight, ChevronLeft, ChevronDown, Star, Sparkles, TrendingUp, Clock, Heart, Calendar, Users, Car, Package, Building2, Utensils, Dumbbell, Scissors, X, Filter, SlidersHorizontal, Navigation, MessageCircle, Loader2, Store, Tag, Layers, Ticket, BookOpen, Stethoscope, UtensilsCrossed, Plane, CarTaxiFront, Wrench, Home, Briefcase, GraduationCap, PartyPopper, ShoppingBag, Laptop, Plus, Check, ShoppingCart, Truck, CalendarDays, Ship, Hotel, Key } from 'lucide-react';
import { useApp, LOGOS } from '../context/AppContext';
import { CATEGORIES, OFFICES, SERVICES, PRODUCTS, COURSES, RENTALS, MODULES, getUpcomingEvents } from '../data/database';
import OfficeCard from '../components/OfficeCard';
import { useI18n } from '../i18n';

// Auto-scrolling slider component with arrows and swipe
const AutoSlider = ({ children, autoScrollInterval = 3000, itemWidth = 300, className = '' }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  
  // Touch handling for swipe
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const updateScrollButtons = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const scroll = useCallback((direction) => {
    if (scrollRef.current) {
      const scrollAmount = itemWidth * 2;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, [itemWidth]);

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) {
      setIsPaused(false);
      return;
    }
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        scroll('right');
      } else {
        scroll('left');
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
    // Resume auto-scroll after a delay
    setTimeout(() => setIsPaused(false), 3000);
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!autoScrollInterval || isPaused) return;
    
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          // Reset to start
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollBy({ left: itemWidth, behavior: 'smooth' });
        }
      }
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [autoScrollInterval, isPaused, itemWidth]);

  useEffect(() => {
    updateScrollButtons();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', updateScrollButtons);
      return () => ref.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons]);

  return (
    <div 
      className={`relative group ${className}`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Left Arrow - Desktop only */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100 -translate-x-1/2"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}
      
      {/* Scrollable Content */}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex gap-4" style={{ width: 'max-content' }}>
          {children}
        </div>
      </div>

      {/* Right Arrow - Desktop only */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100 translate-x-1/2"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      )}
    </div>
  );
};

// Definisce i filtri intelligenti per ogni categoria
const SMART_FILTERS = {
  hotel: {
    type: 'booking',
    fields: [
      { id: 'checkin', label: 'Check-in', type: 'date', icon: Calendar },
      { id: 'checkout', label: 'Check-out', type: 'date', icon: Calendar },
      { id: 'guests', label: 'Ospiti', type: 'number', icon: Users, min: 1, max: 10 },
      { id: 'rooms', label: 'Camere', type: 'number', icon: Building2, min: 1, max: 5 },
    ]
  },
  taxi: {
    type: 'transport',
    fields: [
      { id: 'pickup', label: 'Partenza da', type: 'location', icon: MapPin },
      { id: 'dropoff', label: 'Arrivo a', type: 'location', icon: MapPin },
      { id: 'datetime', label: 'Quando', type: 'datetime', icon: Calendar },
      { id: 'passengers', label: 'Passeggeri', type: 'number', icon: Users, min: 1, max: 8 },
    ]
  },
  ristoranti: {
    type: 'booking',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'guests', label: 'Persone', type: 'number', icon: Users, min: 1, max: 20 },
      { id: 'delivery', label: 'Modalità', type: 'select', icon: Package, options: ['Sul posto', 'Asporto', 'Delivery'] },
    ]
  },
  parrucchieri: {
    type: 'appointment',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'service', label: 'Servizio', type: 'select', icon: Scissors, options: ['Taglio', 'Colore', 'Piega', 'Trattamento'] },
    ]
  },
  estetisti: {
    type: 'appointment',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'service', label: 'Trattamento', type: 'select', icon: Sparkles, options: ['Manicure', 'Pedicure', 'Pulizia viso', 'Massaggio', 'Ceretta'] },
    ]
  },
  palestre: {
    type: 'subscription',
    fields: [
      { id: 'type', label: 'Tipo', type: 'select', icon: Dumbbell, options: ['Abbonamento', 'Singolo ingresso', 'Corso'] },
      { id: 'date', label: 'Data inizio', type: 'date', icon: Calendar },
    ]
  },
  medici: {
    type: 'appointment',
    fields: [
      { id: 'specialty', label: 'Specialità', type: 'select', icon: Building2, options: ['Medico generico', 'Dermatologo', 'Cardiologo', 'Ortopedico', 'Oculista'] },
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'visitType', label: 'Tipo visita', type: 'select', icon: Users, options: ['In studio', 'Videoconsulto', 'Domiciliare'] },
    ]
  },
  default: {
    type: 'general',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'deliveryMode', label: 'Modalità', type: 'select', icon: Package, options: ['Sul posto', 'Ritiro', 'Consegna'] },
    ]
  }
};

// Gruppi di categorie per il modal - usando icone Lucide
import { CategoryIcon, CATEGORY_ICONS, getCategoryColorClass, getCategoryBgClass } from '../components/CategoryIcons';

// Gruppi di categorie per il modal - con icone Lucide
const CATEGORY_GROUPS = {
  bellezza: { name: 'Bellezza e Cura', IconComponent: Sparkles, color: 'pink' },
  salute: { name: 'Salute', IconComponent: Stethoscope, color: 'red' },
  sport: { name: 'Sport e Fitness', IconComponent: Dumbbell, color: 'orange' },
  food: { name: 'Ristorazione', IconComponent: UtensilsCrossed, color: 'amber' },
  travel: { name: 'Viaggi e Alloggi', IconComponent: Plane, color: 'sky' },
  transport: { name: 'Trasporti', IconComponent: CarTaxiFront, color: 'yellow' },
  auto: { name: 'Servizi Auto', IconComponent: Wrench, color: 'slate' },
  casa: { name: 'Casa e Servizi', IconComponent: Home, color: 'teal' },
  professionisti: { name: 'Professionisti', IconComponent: Briefcase, color: 'indigo' },
  formazione: { name: 'Formazione', IconComponent: GraduationCap, color: 'purple' },
  eventi: { name: 'Eventi', IconComponent: PartyPopper, color: 'rose' },
  shopping: { name: 'Shopping', IconComponent: ShoppingBag, color: 'emerald' },
  digitale: { name: 'Digitale', IconComponent: Laptop, color: 'blue' },
  altro: { name: 'Altro', IconComponent: Plus, color: 'gray' }
};

const HomePage = ({ onNavigate }) => {
  const { user } = useApp();
  const { t, tc, tg } = useI18n();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('Napoli');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSmartFilters, setShowSmartFilters] = useState(false);
  const [smartFilterValues, setSmartFilterValues] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState(null);
  
  const locationPickerRef = useRef(null);
  const searchRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const isMouseOverDropdownRef = useRef(false);

  // Desktop Hero location states
  const [desktopSelectedLocation, setDesktopSelectedLocation] = useState('Napoli');
  const [showDesktopLocationPicker, setShowDesktopLocationPicker] = useState(false);
  const [desktopLocationSearch, setDesktopLocationSearch] = useState('');

  // Cities list
  const CITIES = ['Napoli', 'Roma', 'Milano', 'Firenze', 'Torino', 'Bologna', 'Venezia', 'Palermo', 'Genova', 'Bari'];

  const filteredCities = useMemo(() => {
    if (!desktopLocationSearch) return CITIES;
    return CITIES.filter(city => city.toLowerCase().includes(desktopLocationSearch.toLowerCase()));
  }, [desktopLocationSearch]);

  const handleUseCurrentLocation = () => {
    setIsLocating(true);
    setTimeout(() => {
      setDesktopSelectedLocation('Napoli, Centro');
      setShowDesktopLocationPicker(false);
      setIsLocating(false);
    }, 1500);
  };

  const handleLocationSelect = (city) => {
    setDesktopSelectedLocation(city);
    setShowDesktopLocationPicker(false);
  };

  const handleDesktopSearch = (term) => {
    const query = term || searchQuery;
    if (query) {
      setShowSuggestions(false);
      onNavigate('search-results', { query, location: desktopSelectedLocation });
    }
  };

  const trendingServices = (SERVICES || []).filter(s => s.isElit || s.originalPrice).slice(0, 4);
  
  // Categorie principali da mostrare nella home (8)
  const mainCategories = useMemo(() => {
    return (CATEGORIES || []).filter(c => 
      ['parrucchieri', 'ristoranti', 'hotel', 'taxi', 'medici', 'palestre', 'estetisti', 'barbieri'].includes(c.slug)
    );
  }, []);

  // Suggerimenti in tempo reale
  const suggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return { categories: [], businesses: [], services: [], products: [], events: [], courses: [] };
    
    const query = searchQuery.toLowerCase();
    
    // Filtra categorie
    const matchedCategories = (CATEGORIES || []).filter(c => 
      c.name?.toLowerCase().includes(query) || c.slug?.includes(query)
    ).slice(0, 5);
    
    // Filtra aziende
    const matchedBusinesses = (OFFICES || []).filter(b => 
      b.name?.toLowerCase().includes(query) || 
      b.category?.toLowerCase().includes(query) ||
      b.address?.toLowerCase().includes(query)
    ).slice(0, 5);
    
    // Filtra servizi
    const matchedServices = (SERVICES || []).filter(s => 
      s.name?.toLowerCase().includes(query) || 
      s.category?.toLowerCase().includes(query)
    ).slice(0, 5);
    
    // Filtra prodotti
    const matchedProducts = (PRODUCTS || []).filter(p => 
      p.name?.toLowerCase().includes(query) || 
      p.category?.toLowerCase().includes(query)
    ).slice(0, 5);

    // Eventi - modulo non implementato
    const matchedEvents = [];

    // Filtra corsi
    const matchedCourses = (COURSES || []).filter(c => 
      c.name?.toLowerCase().includes(query) || 
      c.category?.toLowerCase().includes(query) ||
      c.level?.toLowerCase().includes(query)
    ).slice(0, 5);
    
    return {
      categories: matchedCategories,
      businesses: matchedBusinesses,
      services: matchedServices,
      products: matchedProducts,
      events: matchedEvents,
      courses: matchedCourses
    };
  }, [searchQuery]);

  const hasSuggestions = suggestions.categories.length > 0 || 
                         suggestions.businesses.length > 0 || 
                         suggestions.services.length > 0 || 
                         suggestions.products.length > 0 ||
                         suggestions.events.length > 0 ||
                         suggestions.courses.length > 0;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationPickerRef.current && !locationPickerRef.current.contains(event.target)) {
        setShowLocationPicker(false);
      }
      // Close suggestions when clicking outside both mobile and desktop search
      const isInsideMobileSearch = searchRef.current && searchRef.current.contains(event.target);
      const isInsideDesktopSearch = desktopSearchRef.current && desktopSearchRef.current.contains(event.target);
      if (!isInsideMobileSearch && !isInsideDesktopSearch) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Geolocation
  const handleGetLocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSelectedLocation('Posizione attuale');
          setShowLocationPicker(false);
          setIsLocating(false);
        },
        (error) => {
          alert('Impossibile rilevare la posizione. Verifica le impostazioni GPS.');
          setIsLocating(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      alert('Geolocalizzazione non supportata dal browser');
      setIsLocating(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      onNavigate('search-results', { 
        query: searchQuery,
        location: selectedLocation,
        category: selectedCategory?.slug,
        filters: smartFilterValues,
        detectFilters: true
      });
    }
  };

  const handleSuggestionClick = (type, item) => {
    switch (type) {
      case 'category':
        // Mostra il box per completare la ricerca (sia mobile che desktop)
        // NON chiudiamo i suggerimenti qui, lo fa handleCategorySelect
        handleCategorySelect(item);
        break;
      case 'business':
        setShowSuggestions(false);
        setSearchQuery('');
        onNavigate('office', { id: item.id });
        break;
      case 'service':
        setShowSuggestions(false);
        setSearchQuery('');
        onNavigate('service', { id: item.id, businessId: item.officeId });
        break;
      case 'product':
        setShowSuggestions(false);
        setSearchQuery('');
        onNavigate('product', { id: item.id, businessId: item.officeId });
        break;
      case 'event':
        setShowSuggestions(false);
        setSearchQuery('');
        onNavigate('event', { id: item.id });
        break;
      case 'course':
        setShowSuggestions(false);
        setSearchQuery('');
        onNavigate('course', { id: item.id });
        break;
      default:
        break;
    }
  };

  const handleCategorySelect = (category) => {
    // Mostra sempre il box per completare la ricerca (sia mobile che desktop)
    setShowSuggestions(false);
    setSelectedCategory(category);
    setShowSmartFilters(true);
    setSmartFilterValues({});
    setShowAllCategories(false);
  };

  const getSmartFilters = () => {
    if (!selectedCategory) return SMART_FILTERS.default;
    return SMART_FILTERS[selectedCategory.slug] || SMART_FILTERS.default;
  };

  const updateFilterValue = (fieldId, value) => {
    setSmartFilterValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setShowSmartFilters(false);
    setSmartFilterValues({});
  };

  // Ordina categorie alfabeticamente per il modal
  const sortedCategories = [...(CATEGORIES || [])].sort((a, b) => a.name.localeCompare(b.name));
  const filteredCategories = sortedCategories.filter(cat => {
    const matchesSearch = cat.name?.toLowerCase().includes(categorySearch.toLowerCase());
    const matchesGroup = !selectedGroupFilter || cat.group === selectedGroupFilter;
    return matchesSearch && matchesGroup;
  });

  // Mix services and products to show variety (not just beauty)
  const variedServices = useMemo(() => {
    // Group services by business to get variety
    const byBusiness = {};
    (SERVICES || []).forEach(s => {
      if (!byBusiness[s.officeId]) byBusiness[s.officeId] = [];
      byBusiness[s.officeId].push(s);
    });
    // Take first from each business, then second, etc.
    const result = [];
    const businesses = Object.keys(byBusiness);
    let index = 0;
    while (result.length < 12 && businesses.some(b => byBusiness[b][index])) {
      businesses.forEach(b => {
        if (byBusiness[b][index] && result.length < 12) {
          result.push(byBusiness[b][index]);
        }
      });
      index++;
    }
    return result;
  }, []);

  const variedProducts = useMemo(() => {
    // Group products by business to get variety
    const byBusiness = {};
    (PRODUCTS || []).forEach(p => {
      if (!byBusiness[p.officeId]) byBusiness[p.officeId] = [];
      byBusiness[p.officeId].push(p);
    });
    const result = [];
    const businesses = Object.keys(byBusiness);
    let index = 0;
    while (result.length < 12 && businesses.some(b => byBusiness[b][index])) {
      businesses.forEach(b => {
        if (byBusiness[b][index] && result.length < 12) {
          result.push(byBusiness[b][index]);
        }
      });
      index++;
    }
    return result;
  }, []);

  // Rentals per la sezione noleggi/soggiorni
  const variedRentals = useMemo(() => {
    const byBusiness = {};
    (RENTALS || []).forEach(r => {
      if (!byBusiness[r.officeId]) byBusiness[r.officeId] = [];
      byBusiness[r.officeId].push(r);
    });
    const result = [];
    const businesses = Object.keys(byBusiness);
    let index = 0;
    while (result.length < 12 && businesses.some(b => byBusiness[b][index])) {
      businesses.forEach(b => {
        if (byBusiness[b][index] && result.length < 12) {
          result.push(byBusiness[b][index]);
        }
      });
      index++;
    }
    return result;
  }, []);

  // Categorizza gli Office per tipo di modulo
  const appointmentOffices = useMemo(() => {
    return OFFICES.filter(o => o.enabledModules?.includes('appointments')).slice(0, 8);
  }, []);

  const rentalOffices = useMemo(() => {
    return OFFICES.filter(o => o.enabledModules?.includes('rentals')).slice(0, 8);
  }, []);

  // Raggruppa categorie
  const groupedCategories = useMemo(() => {
    const groups = {};
    (CATEGORIES || []).forEach(cat => {
      if (!groups[cat.group]) groups[cat.group] = [];
      groups[cat.group].push(cat);
    });
    return groups;
  }, []);

  const renderFilterField = (field) => {
    const IconComponent = field.icon;
    const value = smartFilterValues[field.id] || '';

    switch (field.type) {
      case 'date':
        return (
          <div key={field.id} className="flex-1 min-w-[140px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      case 'time':
        return (
          <div key={field.id} className="flex-1 min-w-[120px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="time"
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      case 'datetime':
        return (
          <div key={field.id} className="flex-1 min-w-[180px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="datetime-local"
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      case 'number':
        return (
          <div key={field.id} className="flex-1 min-w-[100px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                min={field.min || 1}
                max={field.max || 99}
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                placeholder={`${field.min || 1}`}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      case 'select':
        return (
          <div key={field.id} className="flex-1 min-w-[140px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none appearance-none bg-white"
              >
                <option value="">Seleziona</option>
                {field.options?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        );
      case 'location':
        return (
          <div key={field.id} className="flex-1 min-w-[160px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={value}
                onChange={(e) => updateFilterValue(field.id, e.target.value)}
                placeholder="Inserisci indirizzo..."
                className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-8">
      {/* Hero Section - Mobile */}
      <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 pt-4 pb-8 px-4 sm:px-6 lg:hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
        </div>

        {/* Header - Mobile only */}
        <div className="relative max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <img 
              src={LOGOS.light} 
              alt="Skipres" 
              className="h-8 brightness-0 invert"
              data-testid="logo"
            />
            
            <div className="flex items-center gap-3">
              {/* Location Picker */}
              <div className="relative" ref={locationPickerRef}>
                <button 
                  onClick={() => setShowLocationPicker(!showLocationPicker)}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-3 py-2 rounded-full transition-all"
                  data-testid="location-picker-btn"
                >
                  <MapPin className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white max-w-[100px] truncate">{selectedLocation}</span>
                  <ChevronDown className={`w-4 h-4 text-white transition-transform ${showLocationPicker ? 'rotate-180' : ''}`} />
                </button>
                
                {showLocationPicker && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-slide-down">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">{t('hero', 'whereLooking')}</p>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          placeholder={t('hero', 'searchCityPlaceholder')}
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:outline-none"
                          autoFocus
                          data-testid="location-search-input"
                        />
                      </div>
                    </div>
                    
                    <button
                      onClick={handleGetLocation}
                      disabled={isLocating}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-gray-100"
                      data-testid="use-gps-btn"
                    >
                      {isLocating ? (
                        <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
                      ) : (
                        <Navigation className="w-5 h-5 text-emerald-500" />
                      )}
                      <div className="text-left">
                        <p className="font-medium text-emerald-600">{t('hero', 'useMyLocation')}</p>
                        <p className="text-xs text-gray-500">{t('hero', 'autoDetectGps')}</p>
                      </div>
                    </button>
                    
                    <div className="max-h-48 overflow-y-auto">
                      {locationSearch ? (
                        <>
                          <div className="px-4 py-2 bg-gray-50">
                            <p className="text-xs text-gray-500">Risultati per "{locationSearch}"</p>
                          </div>
                          {['Napoli', 'Roma', 'Milano', 'Firenze', 'Torino', 'Bologna', 'Venezia', 'Palermo']
                            .filter(city => city.toLowerCase().includes(locationSearch.toLowerCase()))
                            .map(city => (
                              <button
                                key={city}
                                onClick={() => {
                                  setSelectedLocation(city);
                                  setShowLocationPicker(false);
                                  setLocationSearch('');
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                              >
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-700">{city}</span>
                              </button>
                            ))}
                          {locationSearch.length > 2 && (
                            <button
                              onClick={() => {
                                setSelectedLocation(locationSearch);
                                setShowLocationPicker(false);
                                setLocationSearch('');
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors border-t border-gray-100"
                            >
                              <MapPin className="w-4 h-4 text-emerald-500" />
                              <span className="text-emerald-600">{t('hero', 'use')} "{locationSearch}"</span>
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="px-4 py-2 bg-gray-50">
                            <p className="text-xs text-gray-500">{t('hero', 'popularCities')}</p>
                          </div>
                          {['Napoli', 'Roma', 'Milano', 'Firenze', 'Torino', 'Bologna'].map(city => (
                            <button
                              key={city}
                              onClick={() => {
                                setSelectedLocation(city);
                                setShowLocationPicker(false);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                                selectedLocation === city ? 'bg-emerald-50' : ''
                              }`}
                            >
                              <MapPin className={`w-4 h-4 ${selectedLocation === city ? 'text-emerald-500' : 'text-gray-400'}`} />
                              <span className={selectedLocation === city ? 'text-emerald-600 font-medium' : 'text-gray-700'}>{city}</span>
                              {selectedLocation === city && <div className="ml-auto w-2 h-2 bg-emerald-500 rounded-full" />}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Messenger shortcut - REMOVED */}
            </div>
          </div>

          {/* Greeting */}
          <div className="mb-5 lg:mb-8 lg:text-center">
            <p className="text-emerald-100 text-sm mb-0.5 lg:text-base">{t('hero', 'greeting')} {user.name.split(' ')[0]} 👋</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t('hero', 'whatLooking')}</h1>
          </div>

          {/* Search Bar with Suggestions - Mobile only */}
          <div className="relative lg:hidden" ref={searchRef}>
            <form onSubmit={handleSearch}>
              <div className={`bg-white rounded-2xl shadow-lg shadow-emerald-900/20 overflow-hidden transition-all ${searchFocused ? 'ring-2 ring-white/50' : ''}`}>
                <div className="flex items-center">
                  <div className="flex-1 flex items-center gap-3 px-4 py-3.5">
                    <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSuggestions(true);
                      }}
                      placeholder={t('hero', 'searchPlaceholderMobile')}
                      className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
                      onFocus={() => {
                        setSearchFocused(true);
                        if (searchQuery.length >= 2) setShowSuggestions(true);
                      }}
                      onBlur={() => setSearchFocused(false)}
                      data-testid="main-search-input"
                    />
                    {searchQuery && (
                      <button 
                        type="button" 
                        onClick={() => {
                          setSearchQuery('');
                          setShowSuggestions(false);
                        }}
                        className="p-1 hover:bg-gray-100 rounded-full"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="bg-emerald-500 text-white p-3 m-1.5 rounded-xl hover:bg-emerald-600 transition-colors"
                    data-testid="voice-search-btn"
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && hasSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[60vh] overflow-y-auto animate-slide-down" data-testid="suggestions-dropdown">
                {/* Categories */}
                {suggestions.categories.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Categorie</span>
                    </div>
                    {suggestions.categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => handleSuggestionClick('category', cat)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors"
                        data-testid={`suggestion-category-${cat.slug}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getCategoryBgClass(cat.color)}`}>
                          <CategoryIcon slug={cat.slug} color={cat.color} size="lg" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-900">{cat.name}</p>
                          <p className="text-xs text-gray-500">Categoria</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Businesses */}
                {suggestions.businesses.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <Store className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Attività</span>
                    </div>
                    {suggestions.businesses.map(business => (
                      <button
                        key={business.id}
                        onClick={() => handleSuggestionClick('business', business)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors"
                        data-testid={`suggestion-business-${business.id}`}
                      >
                        <img src={business.image} alt={business.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-gray-900 truncate">{business.name}</p>
                          <p className="text-xs text-gray-500 truncate">{business.category} • {business.address}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="text-sm font-medium text-gray-600">{business.rating}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Services */}
                {suggestions.services.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Servizi</span>
                    </div>
                    {suggestions.services.map(service => {
                      const business = OFFICES.find(b => b.id === service.officeId);
                      return (
                        <button
                          key={service.id}
                          onClick={() => handleSuggestionClick('service', service)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors"
                          data-testid={`suggestion-service-${service.id}`}
                        >
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Scissors className="w-5 h-5 text-purple-500" />
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-gray-900 truncate">{service.name}</p>
                            <p className="text-xs text-gray-500 truncate">{business?.name}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{service.price}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Products */}
                {suggestions.products.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-orange-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Prodotti</span>
                    </div>
                    {suggestions.products.map(product => {
                      const business = OFFICES.find(b => b.id === product.officeId);
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleSuggestionClick('product', product)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors"
                          data-testid={`suggestion-product-${product.id}`}
                        >
                          <img src={product.image} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-gray-900 truncate">{product.name}</p>
                            <p className="text-xs text-gray-500 truncate">{business?.name}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{product.price}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Events */}
                {suggestions.events.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-rose-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Eventi</span>
                    </div>
                    {suggestions.events.map(event => (
                      <button
                        key={event.id}
                        onClick={() => handleSuggestionClick('event', event)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-rose-50 transition-colors"
                        data-testid={`suggestion-event-${event.id}`}
                      >
                        <img src={event.image} alt={event.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-gray-900 truncate">{event.name}</p>
                          <p className="text-xs text-gray-500 truncate">{event.date} • {event.location}</p>
                        </div>
                        <span className="font-bold text-emerald-600">€{event.price}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Courses */}
                {suggestions.courses.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Corsi</span>
                    </div>
                    {suggestions.courses.map(course => (
                      <button
                        key={course.id}
                        onClick={() => handleSuggestionClick('course', course)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors"
                        data-testid={`suggestion-course-${course.id}`}
                      >
                        <img src={course.image} alt={course.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-gray-900 truncate">{course.name}</p>
                          <p className="text-xs text-gray-500 truncate">{course.category} • {course.level}</p>
                        </div>
                        <span className="font-bold text-emerald-600">€{course.price}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* View all results */}
                <button
                  onClick={handleSearch}
                  className="w-full px-4 py-3 bg-emerald-50 text-emerald-600 font-medium hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {t('hero', 'viewAllResults')} "{searchQuery}"
                </button>
              </div>
            )}
          </div>
          
          {/* Smart Filters Panel - Inside Hero Section */}
          {showSmartFilters && selectedCategory && (
            <div className="mt-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden animate-slide-down" data-testid="smart-filters-panel">
              <div className="p-4 bg-gradient-to-r from-emerald-50/80 to-teal-50/80 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getCategoryBgClass(selectedCategory.color)}`}>
                      <CategoryIcon slug={selectedCategory.slug} color={selectedCategory.color} size="md" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      {selectedCategory.name}
                    </h3>
                  </div>
                  <button 
                    onClick={clearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 bg-white/50 px-2 py-1 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                    Chiudi
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="flex flex-wrap gap-3">
                  {getSmartFilters().fields.map(field => renderFilterField(field))}
                </div>
                
                <button 
                  onClick={() => {
                    onNavigate('search-results', { 
                      query: searchQuery,
                      location: selectedLocation,
                      category: selectedCategory?.slug,
                      filters: smartFilterValues,
                      detectFilters: true
                    });
                  }}
                  className="w-full mt-4 bg-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                  data-testid="search-with-filters-btn"
                >
                  <Search className="w-5 h-5" />
                  {t('common', 'search')} {tc(selectedCategory.slug) || selectedCategory.name}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero Section - Desktop - Search Engine Style */}
      <div className="hidden lg:block bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-screen-2xl mx-auto px-8 py-16 xl:py-20 relative">
          <div className="text-center max-w-[65rem] mx-auto">
            {/* Headline */}
            <h1 className="text-4xl xl:text-5xl font-bold text-white mb-3 tracking-tight">
              {t('hero', 'desktopTitle')}
            </h1>
            <p className="text-emerald-100 text-lg mb-10">
              {t('hero', 'desktopSubtitle')}
            </p>
            
            {/* Big Search Bar */}
            <div className="relative z-50 max-w-[65rem] mx-auto" ref={desktopSearchRef}>
              <div className={`flex items-center bg-white rounded-full shadow-2xl transition-all ${
                searchFocused ? 'ring-4 ring-white/30 shadow-white/20' : ''
              }`}>
                {/* Location Picker */}
                <div className="relative">
                  <button
                    onClick={() => setShowDesktopLocationPicker(!showDesktopLocationPicker)}
                    className="flex items-center gap-2 pl-6 pr-4 py-5 text-gray-700 hover:text-gray-900 border-r border-gray-200 transition-colors"
                    data-testid="hero-location-btn"
                  >
                    <MapPin className="w-5 h-5 text-emerald-500" />
                    <span className="font-medium">{desktopSelectedLocation}</span>
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  {/* Location Dropdown */}
                  {showDesktopLocationPicker && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl">
                          <Search className="w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={desktopLocationSearch}
                            onChange={(e) => setDesktopLocationSearch(e.target.value)}
                            placeholder={t('hero', 'searchCity')}
                            className="flex-1 bg-transparent text-sm outline-none"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2">
                        <button
                          onClick={handleUseCurrentLocation}
                          disabled={isLocating}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                        >
                          {isLocating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Navigation className="w-4 h-4" />
                          )}
                          <span className="font-medium text-sm">{t('hero', 'useCurrentLocation')}</span>
                        </button>
                        {filteredCities.map(city => (
                          <button
                            key={city}
                            onClick={() => handleLocationSelect(city)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                              desktopSelectedLocation === city 
                                ? 'bg-emerald-50 text-emerald-700' 
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{city}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Search Input - uses shared searchQuery */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
                    placeholder={t('hero', 'searchPlaceholderDesktop')}
                    className="w-full px-6 py-5 text-gray-900 text-lg placeholder-gray-400 outline-none rounded-full"
                    data-testid="hero-search-input"
                  />
                </div>

                {/* Search Button */}
                <button
                  onClick={() => handleDesktopSearch()}
                  className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-5 rounded-full mr-1.5 transition-colors"
                  data-testid="hero-search-btn"
                >
                  <Search className="w-5 h-5" />
                  <span>{t('common', 'search')}</span>
                </button>
              </div>

              {/* Search Suggestions - same as mobile */}
              {showSuggestions && hasSuggestions && (
                <div 
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[60vh] overflow-y-auto animate-slide-down" 
                  data-testid="desktop-suggestions-dropdown"
                  onMouseEnter={() => { isMouseOverDropdownRef.current = true; }}
                  onMouseLeave={() => { isMouseOverDropdownRef.current = false; }}
                >
                  {/* Categories */}
                  {suggestions.categories.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('stats', 'categories')}</span>
                      </div>
                      {suggestions.categories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => handleSuggestionClick('category', cat)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getCategoryBgClass(cat.color)}`}>
                            <CategoryIcon slug={cat.slug} color={cat.color} size="lg" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-900">{tc(cat.slug) || cat.name}</p>
                            <p className="text-xs text-gray-500">{t('misc', 'category')}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Businesses */}
                  {suggestions.businesses.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <Store className="w-4 h-4 text-blue-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('sections', 'businesses')}</span>
                      </div>
                      {suggestions.businesses.map(business => (
                        <button
                          key={business.id}
                          onClick={() => handleSuggestionClick('business', business)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors"
                        >
                          <img src={business.image} alt={business.name} className="w-10 h-10 rounded-lg object-cover" />
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-gray-900 truncate">{business.name}</p>
                            <p className="text-xs text-gray-500 truncate">{business.category} • {business.address}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                            <span className="text-sm font-medium text-gray-600">{business.rating}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Services */}
                  {suggestions.services.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <Scissors className="w-4 h-4 text-purple-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('sections', 'services')}</span>
                      </div>
                      {suggestions.services.map(service => {
                        const business = OFFICES.find(b => b.id === service.officeId);
                        return (
                          <button
                            key={service.id}
                            onClick={() => handleSuggestionClick('service', service)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors"
                          >
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Scissors className="w-5 h-5 text-purple-500" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium text-gray-900 truncate">{service.name}</p>
                              <p className="text-xs text-gray-500 truncate">{business?.name}</p>
                            </div>
                            <span className="font-bold text-emerald-600">€{service.price}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Products */}
                  {suggestions.products.length > 0 && (
                    <div>
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <Tag className="w-4 h-4 text-orange-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('sections', 'products')}</span>
                      </div>
                      {suggestions.products.map(product => {
                        const business = OFFICES.find(b => b.id === product.officeId);
                        return (
                          <button
                            key={product.id}
                            onClick={() => handleSuggestionClick('product', product)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors"
                          >
                            <img src={product.image} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium text-gray-900 truncate">{product.name}</p>
                              <p className="text-xs text-gray-500 truncate">{business?.name}</p>
                            </div>
                            <span className="font-bold text-emerald-600">€{product.price}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Events */}
                  {suggestions.events.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-rose-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('sections', 'events')}</span>
                      </div>
                      {suggestions.events.map(event => (
                        <button
                          key={event.id}
                          onClick={() => handleSuggestionClick('event', event)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-rose-50 transition-colors"
                        >
                          <img src={event.image} alt={event.name} className="w-10 h-10 rounded-lg object-cover" />
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-gray-900 truncate">{event.name}</p>
                            <p className="text-xs text-gray-500 truncate">{event.date} • {event.location}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{event.price}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Courses */}
                  {suggestions.courses.length > 0 && (
                    <div className="border-b border-gray-100">
                      <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-indigo-500" />
                        <span className="text-xs font-medium text-gray-500 uppercase">{t('sections', 'courses')}</span>
                      </div>
                      {suggestions.courses.map(course => (
                        <button
                          key={course.id}
                          onClick={() => handleSuggestionClick('course', course)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors"
                        >
                          <img src={course.image} alt={course.name} className="w-10 h-10 rounded-lg object-cover" />
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium text-gray-900 truncate">{course.name}</p>
                            <p className="text-xs text-gray-500 truncate">{course.category} • {course.level}</p>
                          </div>
                          <span className="font-bold text-emerald-600">€{course.price}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* View all results */}
                  <button
                    onClick={handleSearch}
                    className="w-full px-4 py-3 bg-emerald-50 text-emerald-600 font-medium hover:bg-emerald-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    {t('hero', 'viewAllResults')}
                  </button>
                </div>
              )}

              {/* Smart Filters Panel - Desktop */}
              {showSmartFilters && selectedCategory && (
                <div className="mt-4 bg-white rounded-2xl shadow-xl overflow-hidden animate-slide-down text-left" data-testid="desktop-smart-filters-panel">
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getCategoryBgClass(selectedCategory.color)}`}>
                          <CategoryIcon slug={selectedCategory.slug} color={selectedCategory.color} size="lg" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">
                            {selectedCategory.name}
                          </h3>
                          <p className="text-sm text-gray-500">Completa la ricerca con i filtri</p>
                        </div>
                      </div>
                      <button 
                        onClick={clearFilters}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 bg-white px-3 py-1.5 rounded-lg shadow-sm"
                      >
                        <X className="w-4 h-4" />
                        Chiudi
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-5">
                    <div className="flex flex-wrap gap-4">
                      {getSmartFilters().fields.map(field => renderFilterField(field))}
                    </div>
                    
                    <button 
                      onClick={() => {
                        onNavigate('search-results', { 
                          query: searchQuery,
                          location: desktopSelectedLocation,
                          category: selectedCategory?.slug,
                          filters: smartFilterValues,
                          detectFilters: true
                        });
                      }}
                      className="w-full mt-5 bg-emerald-500 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 text-lg"
                      data-testid="desktop-search-with-filters-btn"
                    >
                      <Search className="w-5 h-5" />
                      {t('common', 'search')} {tc(selectedCategory.slug) || selectedCategory.name}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="flex items-center justify-center gap-3 mt-10">
              <span className="text-emerald-100 text-sm">{t('hero', 'popular')}:</span>
              {[
                { slug: 'parrucchieri', fallback: 'Parrucchieri' },
                { slug: 'ristoranti', fallback: 'Ristoranti' },
                { slug: 'palestre', fallback: 'Palestre' },
                { slug: 'spa', fallback: 'Spa' },
                { slug: 'dentisti', fallback: 'Dentisti' },
              ].map((item) => (
                <button
                  key={item.slug}
                  onClick={() => { const name = tc(item.slug) || item.fallback; setSearchQuery(name); handleDesktopSearch(name); }}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-medium transition-colors backdrop-blur-sm"
                >
                  {tc(item.slug) || item.fallback}
                </button>
              ))}
            </div>

            {/* Trust Elements */}
            <div className="flex items-center justify-center gap-4 mt-12 pt-8 border-t border-white/20">
              {/* Trustpilot */}
              <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[180px]">
                <div className="w-9 h-9 bg-[#00b67a] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Star className="w-5 h-5 text-white fill-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">4.8 {t('misc', 'onTrustpilot')}</p>
                  <div className="flex gap-0.5 mt-1">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 text-white fill-white" />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Secure Payments */}
              <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[180px]">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">{t('misc', 'securePayments')}</p>
                  <p className="text-emerald-100 text-xs">SSL 256-bit</p>
                </div>
              </div>
              
              {/* Verified Partners */}
              <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[180px]">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">{t('misc', 'verifiedPartners')}</p>
                  <p className="text-emerald-100 text-xs">{t('misc', 'reliable')}</p>
                </div>
              </div>
              
              {/* Support */}
              <div className="flex items-center gap-2.5 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 min-w-[180px]">
                <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">{t('misc', 'support247')}</p>
                  <p className="text-emerald-100 text-xs">{t('misc', 'alwaysAvailable')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-6 pt-6 lg:pt-8">
        {/* Categories - Mobile only (desktop has them in header) */}
        <div className="mb-6 lg:hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">{t('misc', 'categories')}</h2>
            <button 
              onClick={() => setShowAllCategories(true)}
              className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700 transition-colors"
              data-testid="show-all-categories-btn"
            >
              Tutte ({(CATEGORIES || []).length}) <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
            {mainCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat)}
                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all hover:scale-105 active:scale-95 ${
                  selectedCategory?.id === cat.id 
                    ? 'bg-emerald-50 border-emerald-300 shadow-md' 
                    : 'bg-white border-gray-100 hover:border-emerald-200 hover:shadow-md'
                }`}
                data-testid={`category-${cat.slug}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  selectedCategory?.id === cat.id ? 'bg-emerald-100' : getCategoryBgClass(cat.color)
                }`}>
                  <CategoryIcon slug={cat.slug} color={selectedCategory?.id === cat.id ? 'emerald' : cat.color} size="xl" />
                </div>
                <span className={`text-xs font-medium text-center leading-tight ${
                  selectedCategory?.id === cat.id ? 'text-emerald-700' : 'text-gray-700'
                }`}>{tc(cat.slug) || cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 1. Near You - PRIMA SEZIONE dopo le categorie */}
        <div className="mb-6 lg:mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg lg:text-xl font-bold text-gray-900">{t('homepage', 'nearYou')}</h2>
            </div>
            <button 
              onClick={() => onNavigate('search-results', { query: '' })}
              className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
            >
              {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <AutoSlider autoScrollInterval={5000} itemWidth={290}>
            {OFFICES.slice(0, 10).map((office) => (
              <div key={office.id} className="w-[280px] sm:w-[300px] lg:w-[320px] flex-shrink-0">
                <OfficeCard office={office} onClick={() => onNavigate('office', { id: office.id })} />
              </div>
            ))}
          </AutoSlider>
        </div>

        {/* 2. Servizi ad Appuntamento - Prenotazioni Orarie */}
        <div className="mb-6 lg:mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-500" />
              <h2 className="text-lg lg:text-xl font-bold text-gray-900">
                Servizi ad Appuntamento
              </h2>
              <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                Prenotazioni orarie
              </span>
            </div>
            <button 
              onClick={() => onNavigate('search-results', { filter: 'services' })}
              className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
            >
              {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Slider per tutte le dimensioni */}
          <AutoSlider autoScrollInterval={2000} itemWidth={280}>
            {variedServices.map((service) => {
              const business = OFFICES.find(b => b.id === service.officeId);
              return (
                <div 
                  key={service.id}
                  onClick={() => onNavigate('service-detail', { id: service.id })}
                  className="w-[220px] sm:w-[260px] lg:w-[280px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`trending-service-${service.id}`}
                >
                  <div className="relative h-32 lg:h-36">
                    <img 
                      src={service.images?.[0] || service.image || business?.images?.[0] || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'} 
                      alt={service.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                    />
                    {business?.distance && (
                      <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-emerald-500 text-white px-2 py-1 rounded-full text-xs">
                        <Navigation className="w-3 h-3" /> {business.distance}
                      </div>
                    )}
                  </div>
                  <div className="p-3 lg:p-4">
                    <h4 className="font-semibold text-gray-900 text-sm lg:text-base truncate">{service.name}</h4>
                    <p className="text-xs text-gray-500 truncate">{business?.name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-bold text-emerald-600 text-sm lg:text-base">€{service.price}</span>
                      <div className="flex items-center gap-1 text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span className="text-xs">{service.duration} min</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </AutoSlider>
        </div>

        {/* NUOVA SEZIONE: Noleggi e Soggiorni - Prenotazioni Giornaliere */}
        {variedRentals.length > 0 && (
          <div className="mb-6 lg:mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg lg:text-xl font-bold text-gray-900">
                  Noleggi e Soggiorni
                </h2>
                <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  Prenotazioni giornaliere
                </span>
              </div>
              <button 
                onClick={() => onNavigate('search-results', { filter: 'rentals' })}
                className="text-blue-600 text-sm font-medium flex items-center gap-1 hover:text-blue-700"
              >
                {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <AutoSlider autoScrollInterval={4000} itemWidth={320}>
              {variedRentals.map((rental) => {
                const office = OFFICES.find(o => o.id === rental.officeId);
                const price = rental.pricePerNight || rental.pricePerDay;
                const priceLabel = rental.pricePerNight ? '/notte' : '/giorno';
                const isHotel = rental.pricePerNight;
                const isCar = rental.category === 'economy' || rental.category === 'premium' || rental.category === 'suv' || rental.category === 'electric';
                const isBoat = rental.category === 'gozzo' || rental.category === 'yacht' || rental.category === 'gommone' || rental.category === 'catamarano';
                
                return (
                  <div 
                    key={`rental-${rental.id}`}
                    onClick={() => onNavigate('rental-detail', { id: rental.id })}
                    className="w-[300px] sm:w-[320px] lg:w-[340px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-blue-200 hover:shadow-lg transition-all cursor-pointer group"
                    data-testid={`rental-${rental.id}`}
                  >
                    <div className="relative h-40 lg:h-48">
                      <img 
                        src={rental.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'} 
                        alt={rental.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                      />
                      {/* Badge tipo */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                          isHotel ? 'bg-blue-500 text-white' : 
                          isCar ? 'bg-purple-500 text-white' : 
                          isBoat ? 'bg-cyan-500 text-white' : 
                          'bg-emerald-500 text-white'
                        }`}>
                          {isHotel ? <Hotel className="w-3.5 h-3.5" /> : 
                           isCar ? <Car className="w-3.5 h-3.5" /> : 
                           isBoat ? <Ship className="w-3.5 h-3.5" /> : 
                           <Key className="w-3.5 h-3.5" />}
                          {rental.category}
                        </span>
                      </div>
                      {/* Min stay badge */}
                      {(rental.minStay > 1 || rental.minDays > 1) && (
                        <div className="absolute top-3 right-3">
                          <span className="text-xs bg-white/90 backdrop-blur-sm text-gray-700 px-2 py-1 rounded-full">
                            Min {rental.minStay || rental.minDays} {rental.minStay ? 'notti' : 'giorni'}
                          </span>
                        </div>
                      )}
                      {/* Capacity */}
                      {(rental.maxGuests || rental.maxPassengers) && (
                        <div className="absolute bottom-3 left-3">
                          <span className="text-xs bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            Max {rental.maxGuests || rental.maxPassengers}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 text-base truncate">{rental.name}</h4>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{office?.name}</p>
                      {rental.description && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-1">{rental.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div>
                          <span className="font-bold text-blue-600 text-lg">€{price}</span>
                          <span className="text-gray-500 text-sm">{priceLabel}</span>
                        </div>
                        {rental.amenities && (
                          <div className="flex items-center gap-1">
                            {rental.amenities.slice(0, 2).map((amenity, i) => (
                              <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {amenity}
                              </span>
                            ))}
                            {rental.amenities.length > 2 && (
                              <span className="text-[10px] text-gray-400">+{rental.amenities.length - 2}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </AutoSlider>
          </div>
        )}

        {/* SEZIONE EVENTI */}
        {getUpcomingEvents().length > 0 && (
          <div className="mb-6 lg:mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-rose-500" />
                <h2 className="text-lg lg:text-xl font-bold text-gray-900">
                  Prossimi Eventi
                </h2>
                <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                  Esperienze uniche
                </span>
              </div>
              <button 
                onClick={() => onNavigate('search-results', { filter: 'events' })}
                className="text-rose-600 text-sm font-medium flex items-center gap-1 hover:text-rose-700"
              >
                {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <AutoSlider autoScrollInterval={4500} itemWidth={340}>
              {getUpcomingEvents().map((event) => {
                const office = OFFICES.find(o => o.id === event.officeId);
                const spotsLeft = event.maxParticipants - event.currentParticipants;
                const isAlmostFull = spotsLeft <= 5;
                const isFree = event.price === 0;
                
                return (
                  <div 
                    key={`event-${event.id}`}
                    onClick={() => onNavigate('event-detail', { id: event.id })}
                    className="w-[320px] sm:w-[340px] lg:w-[380px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-rose-200 hover:shadow-lg transition-all cursor-pointer group"
                    data-testid={`event-${event.id}`}
                  >
                    <div className="relative h-44 lg:h-52">
                      <img 
                        src={event.image} 
                        alt={event.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                      />
                      {/* Badge categoria */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                          event.category === 'wellness' ? 'bg-purple-500 text-white' : 
                          event.category === 'fitness' ? 'bg-orange-500 text-white' : 
                          event.category === 'workshop' ? 'bg-blue-500 text-white' :
                          'bg-rose-500 text-white'
                        }`}>
                          <PartyPopper className="w-3.5 h-3.5" />
                          {event.category}
                        </span>
                        {isFree && (
                          <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white">
                            GRATIS
                          </span>
                        )}
                      </div>
                      {/* Badge posti */}
                      {isAlmostFull && (
                        <div className="absolute top-3 right-3">
                          <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full animate-pulse">
                            Solo {spotsLeft} posti!
                          </span>
                        </div>
                      )}
                      {/* Data evento */}
                      <div className="absolute bottom-3 left-3">
                        <span className="text-xs bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(event.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                          <span className="text-white/70">•</span>
                          {event.startTime}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 text-base truncate">{event.name}</h4>
                      <p className="text-sm text-gray-500 truncate mt-0.5">{office?.name}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{event.description}</p>
                      <div className="flex items-center justify-between mt-3">
                        <div>
                          {isFree ? (
                            <span className="font-bold text-emerald-600 text-lg">Gratuito</span>
                          ) : (
                            <>
                              <span className="font-bold text-rose-600 text-lg">€{event.price}</span>
                              <span className="text-gray-500 text-sm">/persona</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Users className="w-4 h-4" />
                          <span className="text-sm">{event.currentParticipants}/{event.maxParticipants}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </AutoSlider>
          </div>
        )}

        {/* 3. Popular Products - Slider with shipping info */}
        <div className="mb-6 lg:mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg lg:text-xl font-bold text-gray-900">{t('misc', 'popularProducts')}</h2>
            </div>
            <button 
              onClick={() => onNavigate('search-results', { query: '', filter: 'products' })}
              className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
            >
              {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Slider per tutte le dimensioni */}
          <AutoSlider autoScrollInterval={2800} itemWidth={180}>
            {variedProducts.map((product) => {
              const business = OFFICES.find(b => b.id === product.officeId);
              return (
                <div 
                  key={product.id}
                  onClick={() => onNavigate('product-detail', { id: product.id })}
                  className="w-[160px] sm:w-[180px] lg:w-[220px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`product-${product.id}`}
                >
                  <div className="relative aspect-square">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    {product.discount && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">-{product.discount}%</div>
                    )}
                    {/* Shipping badge */}
                    <div className="absolute bottom-2 left-2 right-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${
                        product.shipping === 'delivery' ? 'bg-green-100 text-green-700' : 
                        product.shipping === 'pickup' ? 'bg-orange-100 text-orange-700' : 
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {product.shipping === 'delivery' ? (
                          <><Truck className="w-3 h-3" /> {t('shipping', 'delivery')}</>
                        ) : product.shipping === 'pickup' ? (
                          <><MapPin className="w-3 h-3" /> {t('shipping', 'pickup')}</>
                        ) : (
                          <><Truck className="w-3 h-3" /> {t('shipping', 'both')}</>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="p-3 lg:p-4">
                    <h4 className="font-medium text-gray-900 text-sm lg:text-base truncate">{product.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{business?.name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="font-bold text-emerald-600 text-sm lg:text-lg">€{product.price}</p>
                      {product.originalPrice && <p className="text-xs text-gray-400 line-through">€{product.originalPrice}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </AutoSlider>
        </div>


        {/* 5. Courses Section - Slider with location */}
        {COURSES && COURSES.length > 0 && (
          <div className="mb-6 lg:mb-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg lg:text-xl font-bold text-gray-900">{t('misc', 'coursesForYou')}</h2>
              </div>
              <button 
                onClick={() => onNavigate('search-results', { query: '', filter: 'courses' })}
                className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
              >
                {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            {/* Slider per tutte le dimensioni */}
            <AutoSlider autoScrollInterval={4500} itemWidth={280}>
              {COURSES.map((course) => (
                <div 
                  key={course.id}
                  onClick={() => onNavigate('course-detail', { id: course.id })}
                  className="w-[260px] sm:w-[280px] lg:w-[300px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-indigo-200 hover:shadow-lg transition-all cursor-pointer group"
                >
                  <div className="relative h-32 lg:h-40 overflow-hidden">
                    <img src={course.image} alt={course.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-indigo-500 text-white text-xs font-medium px-2 py-1 rounded-full">{course.category}</span>
                      <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-2 py-1 rounded-full">{course.level}</span>
                    </div>
                    {/* Location badge */}
                    <div className="absolute bottom-3 left-3 flex gap-1.5">
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                        course.locationType === 'online' ? 'bg-blue-500 text-white' : 'bg-white/90 backdrop-blur-sm text-gray-700'
                      }`}>
                        {course.locationType === 'online' ? (
                          <><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span> Online</>
                        ) : (
                          <><MapPin className="w-3 h-3" /> {course.city}</>
                        )}
                      </span>
                      {/* Distance badge for in-person courses */}
                      {course.locationType === 'in-person' && course.distance && (
                        <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1 bg-emerald-500 text-white">
                          <Navigation className="w-3 h-3" /> {course.distance}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 lg:p-4">
                    <h4 className="font-semibold text-gray-900 truncate text-sm lg:text-base">{course.name}</h4>
                    <div className="flex items-center gap-2 lg:gap-3 mt-2 text-xs lg:text-sm text-gray-500">
                      <span>{course.totalHours} ore</span>
                      <span>•</span>
                      <span>{course.lessons} {t('misc', 'lessons')}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 lg:mt-3">
                      <p className="font-bold text-indigo-600 text-sm lg:text-lg">€{course.price}</p>
                      {course.originalPrice && <p className="text-xs text-gray-400 line-through">€{course.originalPrice}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </AutoSlider>
          </div>
        )}
      </div>

      {/* All Categories Modal */}
      {showAllCategories && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowAllCategories(false)}>
          <div 
            className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[85vh] overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 z-10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold text-gray-900">{t('hero', 'allCategories')} ({(CATEGORIES || []).length})</h3>
                <button 
                  onClick={() => setShowAllCategories(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Cerca categoria..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:outline-none"
                  autoFocus
                  data-testid="category-search-input"
                />
              </div>
              
              {/* Group filters */}
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  onClick={() => setSelectedGroupFilter(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    !selectedGroupFilter ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Tutte
                </button>
                {Object.entries(CATEGORY_GROUPS).map(([key, group]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedGroupFilter(key === selectedGroupFilter ? null : key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      selectedGroupFilter === key ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <group.IconComponent className={`w-3.5 h-3.5 ${selectedGroupFilter === key ? 'text-white' : getCategoryColorClass(group.color)}`} />
                    {tg(group.name) || group.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filteredCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat)}
                    className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                    data-testid={`all-category-${cat.slug}`}
                  >
                    <div className={`w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm ${getCategoryBgClass(cat.color)}`}>
                      <CategoryIcon slug={cat.slug} color={cat.color} size="lg" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center leading-tight">{tc(cat.slug) || cat.name}</span>
                  </button>
                ))}
              </div>
              
              {filteredCategories.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Nessuna categoria trovata</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
